// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import clsx from 'clsx';
import { useUniqueId } from '@cloudscape-design/component-toolkit/internal';
import { getNavigationActionDetail } from './analytics-metadata/utils';
import analyticsSelectors from './analytics-metadata/styles.css.js';
import styles from './styles.css.js';
export const StepStatusValues = {
    Active: 'active',
    Visited: 'visited',
    Unvisited: 'unvisited',
    Next: 'next',
};
export function getStepStatus(index, activeStepIndex, farthestStepIndex, isLoadingNextStep, allowSkipTo, steps) {
    var _a, _b;
    if (activeStepIndex === index) {
        return StepStatusValues.Active;
    }
    if (isLoadingNextStep) {
        return StepStatusValues.Unvisited;
    }
    if (farthestStepIndex >= index) {
        return StepStatusValues.Visited;
    }
    if (allowSkipTo && index > activeStepIndex) {
        // All steps between current and target are optional — can skip over them
        if (canSkip(activeStepIndex + 1, index, steps)) {
            return StepStatusValues.Next;
        }
        // If either the current step or the next step is optional, we can navigate to it
        if (index === activeStepIndex + 1 && (((_a = steps[index]) === null || _a === void 0 ? void 0 : _a.isOptional) || ((_b = steps[activeStepIndex]) === null || _b === void 0 ? void 0 : _b.isOptional))) {
            return StepStatusValues.Next;
        }
    }
    return StepStatusValues.Unvisited;
}
export function canSkip(fromIndex, toIndex, steps) {
    // Can't skip if there are no steps to skip over
    if (fromIndex >= toIndex) {
        return false;
    }
    for (let i = fromIndex; i < toIndex; i++) {
        if (!steps[i].isOptional) {
            return false;
        }
    }
    return true;
}
export function handleStepNavigation(stepIndex, status, onStepClick, onSkipToClick) {
    if (status === StepStatusValues.Visited) {
        onStepClick(stepIndex);
    }
    else if (status === StepStatusValues.Next) {
        onSkipToClick(stepIndex);
    }
}
export default function WizardStepList({ activeStepIndex, farthestStepIndex, allowSkipTo, i18nStrings, isLoadingNextStep, onStepClick, onSkipToClick, steps, }) {
    return (React.createElement("ul", { className: styles.refresh }, steps.map((step, index) => (React.createElement(WizardStepListItem, { key: index, index: index, step: step, activeStepIndex: activeStepIndex, farthestStepIndex: farthestStepIndex, allowSkipTo: allowSkipTo, i18nStrings: i18nStrings, isLoadingNextStep: isLoadingNextStep, onStepClick: onStepClick, onSkipToClick: onSkipToClick, steps: steps })))));
}
function WizardStepListItem({ index, step, activeStepIndex, farthestStepIndex, allowSkipTo, i18nStrings, isLoadingNextStep, onStepClick, onSkipToClick, steps, }) {
    var _a, _b;
    const status = getStepStatus(index, activeStepIndex, farthestStepIndex, isLoadingNextStep, allowSkipTo, steps);
    const isClickable = status === StepStatusValues.Visited || status === StepStatusValues.Next;
    const stepLabel = (_a = i18nStrings.stepNumberLabel) === null || _a === void 0 ? void 0 : _a.call(i18nStrings, index + 1);
    const fullStepLabel = `${stepLabel}: ${step.title}`;
    const optionalDescriptionId = useUniqueId('wizard-step-optional-');
    const state = {
        active: 'active',
        unvisited: 'disabled',
        visited: 'enabled',
        next: 'enabled',
    }[status];
    const handleInteraction = () => handleStepNavigation(index, status, onStepClick, onSkipToClick);
    return (React.createElement("li", { className: clsx(styles[`${state}`], styles['navigation-link-item']) },
        React.createElement("hr", null),
        React.createElement("span", { className: clsx(styles.number, styles['navigation-link-label']) }, (_b = i18nStrings.stepNumberLabel) === null || _b === void 0 ? void 0 :
            _b.call(i18nStrings, index + 1),
            step.isOptional && React.createElement("i", { id: optionalDescriptionId }, ` - ${i18nStrings.optional}`)),
        React.createElement("a", { className: clsx(styles['navigation-link'], {
                [styles['navigation-link-active']]: status === StepStatusValues.Active,
                [styles['navigation-link-disabled']]: status === StepStatusValues.Unvisited,
            }), "aria-current": status === StepStatusValues.Active ? 'step' : undefined, "aria-disabled": status === StepStatusValues.Unvisited ? 'true' : undefined, onClick: event => {
                event.preventDefault();
                handleInteraction();
            }, onKeyDown: event => {
                if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                }
                // Enter activates the button on key down instead of key up.
                if (event.key === 'Enter') {
                    handleInteraction();
                }
            }, onKeyUp: event => {
                // Emulate button behavior, which also fires on space.
                if (event.key === ' ') {
                    handleInteraction();
                }
            }, role: "button", tabIndex: isClickable ? 0 : undefined, "aria-label": fullStepLabel, "aria-describedby": step.isOptional ? optionalDescriptionId : undefined, ...(status === StepStatusValues.Unvisited
                ? {}
                : getNavigationActionDetail(index, 'step', true, `.${analyticsSelectors['step-title']}`)) },
            React.createElement("div", { className: styles.circle }),
            React.createElement("span", { className: clsx(styles.title, analyticsSelectors['step-title']) }, step.title))));
}
//# sourceMappingURL=wizard-step-list.js.map